import {useQuery} from '@tanstack/react-query';
import {api} from '@/shared/services/api-client';
import {queryKeys} from '@/lib/query-keys';
import {getCached, setCached, REFERENCE_DATA_CACHE_TTL_MS} from '@/shared/lib/localStorageCache';

/**
 * Shared, cross-feature reference-data queries (moderators directory, session
 * types by project). Lives in @/shared so any feature can use one TanStack
 * Query cache entry instead of each issuing its own raw fetch — several
 * features/pages were each independently re-fetching the same data within one
 * session before this was extracted here.
 *
 * Also persisted to localStorage (10-minute TTL): TanStack Query's cache is
 * in-memory only, so it's empty again after a full page reload/new tab. The
 * localStorage layer is purely an optimization on top of that — it's checked
 * before the network call and refreshed after one, but the backend remains
 * the source of truth (pass `refresh: true` to bypass it, e.g. right after a
 * mutation that must observe its own fresh write).
 */

export const moderatorsListQueryKey = queryKeys.moderators.list();

const MODERATORS_LIST_CACHE_KEY = 'referenceData:moderatorsList';

export async function fetchModeratorsList(options?: {refresh?: boolean}): Promise<any[]> {
  if (!options?.refresh) {
    const cached = getCached<any[]>(MODERATORS_LIST_CACHE_KEY, REFERENCE_DATA_CACHE_TTL_MS);
    if (cached) return cached;
  }

  const res = await api.moderators.list({refresh: options?.refresh});
  const data = res.data as {moderators?: any[]} | any[] | undefined;
  let list: any[];
  if (data && typeof data === 'object' && !Array.isArray(data) && 'moderators' in data) {
    const m = (data as {moderators?: any[]}).moderators;
    list = Array.isArray(m) ? m : [];
  } else if (Array.isArray(data)) {
    list = data;
  } else {
    list = [];
  }
  setCached(MODERATORS_LIST_CACHE_KEY, list);
  return list;
}

export function useModeratorsListQuery(options?: {enabled?: boolean}) {
  return useQuery({
    queryKey: moderatorsListQueryKey,
    queryFn: () => fetchModeratorsList(),
    enabled: options?.enabled ?? true,
  });
}

export function sessionTypesDetailedByProjectQueryKey(projectId: string) {
  return queryKeys.sessionTypes.detailedByProject(projectId);
}

function sessionTypesCacheKey(projectId: string) {
  return `referenceData:sessionTypesByProject:${projectId}`;
}

/** Raw detailed session types for a project. */
export async function fetchDetailedSessionTypesByProject(
  projectId: string,
  options?: {refresh?: boolean},
): Promise<any[]> {
  const cacheKey = sessionTypesCacheKey(projectId);
  if (!options?.refresh) {
    const cached = getCached<any[]>(cacheKey, REFERENCE_DATA_CACHE_TTL_MS);
    if (cached) return cached;
  }

  const res = await api.sessionTypes.detailedByProject(projectId, {refresh: options?.refresh});
  const payload = res.data as {data?: unknown} | unknown;
  const rawList =
    (payload && typeof payload === 'object' && 'data' in (payload as object)
      ? (payload as {data: unknown}).data
      : payload) ?? [];
  const list = Array.isArray(rawList) ? rawList : [];
  setCached(cacheKey, list);
  return list;
}

export function useDetailedSessionTypesByProjectQuery(
  projectId: string | undefined,
  options?: {enabled?: boolean},
) {
  return useQuery({
    queryKey: sessionTypesDetailedByProjectQueryKey(projectId ?? ''),
    queryFn: () => fetchDetailedSessionTypesByProject(projectId!),
    enabled: (options?.enabled ?? true) && !!projectId,
  });
}
